import { Button, Spin } from 'antd';
import { ReactComponent as ArrowDown } from '../../../../assets/icons/chevron-down.svg';
import AIDrivenSearchIcon from '../../../../assets/icons/AIDrivenSearchIcon.png';
import doc from '../../../../assets/documents/Δαπάνες Επένδυσης.xlsm';
import { Constants } from 'utils/constants';
import styles from './AIDrivenSearch.module.scss';

/**
 * @component
 *
 * @summary
 * AI Driven Search layout component with children-based composition.
 *
 * @description
 * The `AIDrivenSearch` component provides the presentation layer (UI) for the AI-driven
 * program search feature. It renders the header, explanation text, upload section, and
 * search button, while accepting a **children prop** for flexible upload component composition.
 *
 * This component enables users to upload their "Δαπάνες Επένδυσης" (Investment Expenses)
 * Excel file to find the most suitable funding programs, powered by backend AI matching.
 *
 * **This component is stateless** and relies entirely on external state and event handlers,
 * typically provided by the `useAIDrivenSearch` hook, which manages all reducer-based
 * business logic.
 *
 * @pattern
 * - **Container–Presentational (Smart/Dumb)**:
 *   This component is the *presentational* layer. It receives state and handlers via props
 *   and delegates all logic to its container (custom hook).
 *
 * - **Children Component Composition Pattern**:
 *   Accepts a `children` prop (typically `<AIDrivenSearchUpload />`) for flexible,
 *   reusable upload component composition. This allows the upload implementation to be
 *   swapped or customized without modifying the parent layout.
 *
 * - **Reducer-Driven Custom Hook Integration**:
 *   Pairs with the `useAIDrivenSearch` hook (for now only from `Funding` page), which implements a reducer-driven state
 *   machine for file upload and async search flow.
 *
 * @features
 * - Structured layout with header, explanation, upload section, and search action
 * - Children-based composition for upload component flexibility
 * - Download link placeholder for the official Excel template
 * - Loading state visualization during AI search
 * - Fully controlled by external state/handlers (stateless presentation)
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Upload component to render in the expenses section
 * (typically `<AIDrivenSearchUpload />`).
 * @param {string|null} [props.fileId=null] - File identifier if file uploaded sucessfully. It was received from file upload response.
 * @param {boolean} [props.isSearching=false] - Whether an AI-powered search is currently running.
 * When `true`, displays a loading spinner in the search button.
 * @param {() => Promise<void>} props.onSubmit - Callback triggered when the user clicks
 * the "Αναζήτηση" (Search) button to initiate AI matching.
 * @returns {JSX.Element} The AI Driven Search UI section with header, upload area (children),
 * and search button.
 *
 * @example
 * // Basic usage with useAIDrivenSearch hook
 * import { AIDrivenSearch, AIDrivenSearchUpload } from './components';
 * import { useAIDrivenSearch } from './hooks';
 *
 * export function Funding() {
 *   const { state, handlers } = useAIDrivenSearch();
 *
 *   return (
 *     <AIDrivenSearch
 *       isSearching={state.isSearching}
 *       onSubmit={handlers.handleSubmit}
 *     >
 *       <AIDrivenSearchUpload
 *         error={state.error}
 *         percent={state.percent}
 *         disabled={state.isUploadingFile}
 *         uploadedFile={state.uploadedFile}
 *         onUpload={handlers.handleFileUpload}
 *         onDelete={handlers.handleFileDeletion}
 *       />
 *     </AIDrivenSearch>
 *   );
 * }
 *
 * @example
 * // Custom upload component via children
 * <AIDrivenSearch isSearching={false} onSubmit={handleSearch}>
 *   <CustomFileUploader accept=".xlsx" onChange={handleUpload} />
 * </AIDrivenSearch>
 */
export function AIDrivenSearch(props) {
  const { onSubmit, isSearching = false, fileId = null, children } = props;

  const handleSearch = () => {
    onSubmit(fileId);
  };

  return (
    <section className={styles.container}>
      <div className={styles['content-wrapper']}>
        <section className={styles.header}>
          <img src={AIDrivenSearchIcon} alt="icon" id={styles['header-icon']} />
          <section id={styles['header-text']}>
            <span id={styles['header-upper-title']}>
              {Constants.AIDrivenSearch}
            </span>
            <span id={styles['header-lower-title']}>{Constants.PoweredBy}</span>
          </section>
        </section>
        <section className={styles['explanation-paragraph']}>
          <span>{Constants.DrivenSearchExplanation}</span>
        </section>
        <section className={styles.expenses}>
          <span id={styles['expenses-header-left']}>
            {Constants.Expenses} *
          </span>
          <ArrowDown
            className="colored-svg blue"
            id={styles['expenses-header-right']}
          />
          <section id={styles['expenses-main']}>{children}</section>
          <p id={styles['expenses-footer']}>
            {Constants.DownloadSample}{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={doc}
              download={`${Constants.AISearchFileName}.xlsm`}
            >
              {Constants.Here}
            </a>
          </p>
        </section>
        <Button
          className={styles.footer}
          onClick={handleSearch}
          disabled={!fileId || isSearching}
        >
          {isSearching === true ? <Spin size="default" /> : Constants.Search}
        </Button>
      </div>
    </section>
  );
}
